import {
    createContext,
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { isEqual, omitBy } from "lodash";
import { useParams } from "react-router-dom";
import { noop } from "../../helpers/general";
import { ActionCategory } from "../../common/types/actions";

export interface ActionFilters {
    entityIds?: number[];
}

interface ActionsPageParms {
    category: ActionCategory;
}

export interface ActionsContext {
    filters: ActionFilters;
    category: ActionCategory;
    updateFilters: (value: ActionFilters) => void;
}

export const actionsContext = createContext<ActionsContext>({
    filters: {},
    category: ActionCategory.PERSONALIZE,
    updateFilters: noop,
});

const DEFAULT_FILTERS: ActionFilters = {};

export const ActionsProvider: FC<PropsWithChildren> = ({ children }) => {
    const { category = ActionCategory.PERSONALIZE } =
        useParams() as Readonly<ActionsPageParms>;
    const [filters, setFilters] = useState<ActionFilters>(DEFAULT_FILTERS);

    const updateFilters = useCallback(
        (value: ActionFilters) => {
            const newFilters = omitBy(
                { ...filters, ...value },
                (v) => v === null || v === undefined,
            );

            if (!isEqual(newFilters, filters)) {
                setFilters(newFilters);
            }
        },
        [filters],
    );

    const value = useMemo(
        () => ({
            filters,
            category,
            updateFilters,
        }),
        [category, filters, updateFilters],
    );

    return (
        <actionsContext.Provider value={value}>
            {children}
        </actionsContext.Provider>
    );
};

export function useActionsContext() {
    const ctx = useContext(actionsContext);

    if (!ctx) {
        throw new Error(
            "useActionsContext must be used within an ActionsProvider",
        );
    }

    return ctx;
}
